import { useState, useRef, useEffect } from "react";
import api from "../lib/api";
import { Bot, Send, Sparkles, User } from "lucide-react";

const SUGGESTIONS = [
  "Which leases expire in the next 90 days?",
  "Which tenants owe rent?",
  "Which property has the highest cash flow?",
  "Show upcoming insurance renewals.",
  "Summarize open maintenance requests.",
  "Generate a monthly owner report.",
];

const mid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

export default function AIAssistantPage() {
  const [msgs, setMsgs] = useState([
    { id: "intro", role: "ai", text: "I'm your Kora Holding portfolio analyst. Ask me anything about your portfolio — leases, rent, cash flow, deadlines, or which property needs attention." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const send = async (q) => {
    const text = (q ?? input).trim();
    if (!text || loading) return;
    setMsgs((m) => [...m, { id: mid(), role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text });
      setMsgs((m) => [...m, { id: mid(), role: "ai", text: data.reply }]);
    } catch (error) {
      console.error("AI chat error:", error);
      setMsgs((m) => [...m, { id: mid(), role: "ai", text: "Sorry, I couldn't process that right now." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-8rem)]" data-testid="page-ai">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-white">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">AI Assistant</h1>
          <p className="text-sm text-muted-foreground">Portfolio intelligence, powered by your live data</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto rounded-2xl border border-border bg-card p-5 space-y-4">
        {msgs.map((m) => (
          <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "ai" && <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Bot className="w-4 h-4" /></div>}
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"}`}>{m.text}</div>
            {m.role === "user" && <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center shrink-0"><User className="w-4 h-4" /></div>}
          </div>
        ))}
        {loading && <div className="text-sm text-muted-foreground animate-pulse ml-11">Analyzing portfolio…</div>}
        {msgs.length === 1 && (
          <div className="grid sm:grid-cols-2 gap-2 pt-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid="ai-suggestion" className="text-left text-sm px-3 py-2.5 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-colors">{s}</button>
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="mt-4 flex gap-2">
        <input
          data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask about your portfolio..."
          className="flex-1 px-4 py-3 rounded-xl border border-input bg-card focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        />
        <button onClick={() => send()} disabled={loading} data-testid="ai-send" className="bg-primary text-primary-foreground px-5 rounded-xl disabled:opacity-50 hover:bg-primary/90 transition-colors">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
